import React, { useCallback, useRef, useState } from 'react';
import { FormikProps } from 'formik';

import { searchAddressByCep } from '../../../../services/cep/searchAddressByCep';

import { captureException } from '../helpers/captureException';
import { useFieldError } from '../hooks/useFieldError';
import { IPaymentLinkFormValues } from '../types/types';

const CEP_LENGTH = 8;

const FALLBACK_MESSAGE =
  'Não foi possível buscar o endereço automaticamente. Preencha os campos abaixo.';


export const useBillingAddressSectionBootstrap = (
  form: FormikProps<IPaymentLinkFormValues>,
) => {
  const [loadingCep, setLoadingCep] = useState(false);
  const [cepMessage, setCepMessage] = useState('');
  const autofilledCepRef = useRef<string | null>(null);

  const { setFieldError, setFieldValue, handleChange, handleBlur } = form;

  const hasError = useFieldError(form);

  const handleCepChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleChange(event);

      setCepMessage('');
      setFieldError('zipCode', undefined);

      const cep = event.target.value.replace(/\D/g, '');

      if (
        autofilledCepRef.current !== null &&
        autofilledCepRef.current !== cep
      ) {
        autofilledCepRef.current = null;
        setFieldValue('state', '');
        setFieldValue('city', '');
        setFieldValue('address', '');
        setFieldValue('neighborhood', '');
      }
    },
    [handleChange, setFieldError, setFieldValue],
  );

  const handleCepBlur = useCallback(
    async (event: React.FocusEvent<HTMLInputElement>) => {
      handleBlur(event);

      const cep = event.target.value.replace(/\D/g, '');

      if (cep.length !== CEP_LENGTH) {
        return;
      }

      setLoadingCep(true);
      setCepMessage('');

      try {
        const result = await searchAddressByCep(cep);

        if (result.success === false) {
          const errorMessage =
            'error' in result
              ? result.error
              : 'Não foi possível localizar o CEP';

          setFieldError('zipCode', errorMessage);

          return;
        }

        if (result.fallback) {
          setCepMessage(FALLBACK_MESSAGE);

          return;
        }

        const { address, district, city, state } = result.data;

        setFieldError('zipCode', undefined);
        setFieldValue('state', state || '');
        setFieldValue('city', city || '');
        setFieldValue('address', address || '');
        setFieldValue('neighborhood', district || '');
        autofilledCepRef.current = cep;
      } catch (error) {
        captureException('cep_lookup', error);
        setCepMessage(FALLBACK_MESSAGE);
      } finally {
        setLoadingCep(false);
      }
    },
    [handleBlur, setFieldError, setFieldValue],
  );

  const showZipCodeError = !loadingCep && hasError('zipCode');
  const showCepMessage = !loadingCep && Boolean(cepMessage);

  const zipCodeDescribedBy =
    [
      loadingCep ? 'payment-zip-code-status' : null,
      showZipCodeError ? 'payment-zip-code-error' : null,
      showCepMessage ? 'payment-zip-code-hint' : null,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  return {
    loadingCep,
    cepMessage,
    handleCepChange,
    handleCepBlur,

    zipCodeDescribedBy,
    showZipCodeError,
    showCepMessage,
    showStateError: hasError('state'),
    showCityError: hasError('city'),
    showAddressError: hasError('address'),
    showAddressNumberError: hasError('addressNumber'),
    showNeighborhoodError: hasError('neighborhood'),
  };
};

export default useBillingAddressSectionBootstrap;
